// components/shared/AdminPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { productService } from '../../services/productService';
import { DB } from '../../utils/db';

export function AdminPanel({ onRefresh }) {
  const { brand } = useBrand();
  const toast = useToast();
  const [tab, setTab] = useState('prescriptions');
  const [prescriptions, setPrescriptions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rxFilter, setRxFilter] = useState('all');
  const [orderFilter, setOrderFilter] = useState('all');
  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [adminReply, setAdminReply] = useState('');
  const [productSearch, setProductSearch] = useState({});
  const [productResults, setProductResults] = useState({});
  const [productSearchLoading, setProductSearchLoading] = useState({});
  const [expandedRx, setExpandedRx] = useState(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    const rxs = await DB.get('d2p_prescriptions') || [];
    const ords = await DB.get('d2p_orders') || [];
    const threads = await DB.get('d2p_message_threads') || [];
    setPrescriptions(rxs);
    setOrders(ords);
    setMessageThreads(threads);
  };
  
  const searchProducts = useCallback(async (rxId, phrase) => {
    if (!phrase || phrase.length < 2) {
      setProductResults(prev => ({ ...prev, [rxId]: [] }));
      return;
    }
    setProductSearchLoading(prev => ({ ...prev, [rxId]: true }));
    try {
      const result = await productService.searchProducts({ phrase, limit: 8 });
      setProductResults(prev => ({ ...prev, [rxId]: result?.hits || [] }));
    } catch (err) {
      console.warn('Product search failed:', err.message);
      setProductResults(prev => ({ ...prev, [rxId]: [] }));
    } finally {
      setProductSearchLoading(prev => ({ ...prev, [rxId]: false }));
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (expandedRx && productSearch[expandedRx]) {
        searchProducts(expandedRx, productSearch[expandedRx]);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [productSearch, expandedRx, searchProducts]);

  const mapProductToRx = async (rxId, product) => {
    const updated = prescriptions.map(rx =>
      rx.id === rxId
        ? {
          ...rx,
          productSku: product.sku,
          productId: product.id,
          productName: product.title,
          productPrice: product.price?.price || product.price?.specialPrice || 0,
          productThumbnail: product.thumbnailUrl || ''
        }
        : rx
    );
    await DB.set('d2p_prescriptions', updated);
    setPrescriptions(updated);
    setExpandedRx(null);
    setProductSearch(prev => ({ ...prev, [rxId]: '' }));
    setProductResults(prev => ({ ...prev, [rxId]: [] }));
    toast.addToast(`Mapped to ${product.title}`);
    onRefresh && onRefresh();
  };

  const unmapProductFromRx = async (rxId) => {
    const updated = prescriptions.map(rx =>
      rx.id === rxId
        ? { ...rx, productSku: undefined, productId: undefined, productName: undefined, productPrice: undefined, productThumbnail: undefined }
        : rx
    );
    await DB.set('d2p_prescriptions', updated);
    setPrescriptions(updated);
    toast.addToast('Product mapping removed');
    onRefresh && onRefresh();
  };

  const verifyPrescription = async (rxId) => {
    const rx = prescriptions.find(r => r.id === rxId);
    if (!rx?.productSku) {
      toast.addToast('Please map a product before verifying', 'error');
      return;
    }
    const updated = prescriptions.map(r =>
      r.id === rxId
        ? { ...r, status: 'verified', verifiedAt: new Date().toISOString(), refills: 3 }
        : r
    );
    await DB.set('d2p_prescriptions', updated);
    setPrescriptions(updated);
    toast.addToast('Prescription verified with product mapping');
    onRefresh && onRefresh();
  };
  
  const rejectPrescription = async (rxId) => {
    const updated = prescriptions.map(rx => 
      rx.id === rxId 
        ? { ...rx, status: 'rejected', reviewedAt: new Date().toISOString() }
        : rx
    );
    await DB.set('d2p_prescriptions', updated);
    setPrescriptions(updated);
    toast.addToast('Prescription rejected', 'error');
    onRefresh && onRefresh();
  };
  
  const advanceOrderStatus = async (orderId) => {
    const statusFlow = ['submitted', 'processing', 'shipped', 'delivered'];
    const updated = orders.map(order => {
      if (order.id === orderId) {
        const currentIndex = statusFlow.indexOf(order.status);
        if (currentIndex < statusFlow.length - 1) {
          const newStatus = statusFlow[currentIndex + 1];
          const newTimeline = [
            ...order.timeline,
            { status: newStatus, timestamp: new Date().toISOString() }
          ];
          
          if (newStatus === 'shipped') {
            return {
              ...order,
              status: newStatus,
              timeline: newTimeline,
              trackingNumber: `UPS-1Z999AA10${Date.now().toString().slice(-9)}`
            };
          }
          
          return { ...order, status: newStatus, timeline: newTimeline };
        }
      }
      return order;
    });
    
    await DB.set('d2p_orders', updated);
    setOrders(updated);
    toast.addToast('Order status updated');
    onRefresh && onRefresh();
  };
  
  const handleAdminReply = async () => {
    if (!adminReply.trim() || !selectedThread) return;
    const message = {
      id: `msg_${Date.now()}`,
      sender: 'admin',
      content: adminReply.trim(),
      timestamp: new Date().toISOString()
    };
    const updatedThreads = messageThreads.map(t => {
      if (t.id === selectedThread.id) {
        return { ...t, messages: [...t.messages, message], lastMessageAt: message.timestamp };
      }
      return t;
    });
    await DB.set('d2p_message_threads', updatedThreads);
    setMessageThreads(updatedThreads);
    setSelectedThread(updatedThreads.find(t => t.id === selectedThread.id));
    setAdminReply('');
    toast.addToast('Reply sent');
  };

  const pendingRxs = prescriptions.filter(rx => 
    rx.status === 'pending_review' || rx.status === 'under_review'
  );
  const activeOrders = orders.filter(o => 
    o.status !== 'delivered' && o.status !== 'cancelled'
  );
  const filteredRxs = rxFilter === 'all' ? prescriptions : prescriptions.filter(rx => rx.status === rxFilter);
  const filteredOrders = orderFilter === 'all' ? orders : orders.filter(o => o.status === orderFilter);
  const unreadThreads = messageThreads.filter(t => {
    const lastMsg = t.messages[t.messages.length - 1];
    return lastMsg && lastMsg.sender === 'patient';
  });
  
  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: '50vh',
      background: 'white',
      borderTop: `4px solid ${brand.c.accent}`,
      boxShadow: '0 -4px 24px rgba(0,0,0,0.1)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 24px',
        borderBottom: '2px solid #E5E7EB',
        background: '#F9FAFB',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            background: brand.logo ? 'transparent' : brand.c.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            {brand.logo ? (
              <img src={brand.logo} alt={brand.name} style={{ width: 32, height: 32, objectFit: 'contain' }} />
            ) : (
              <Icon name="shield" size={18} color="white" />
            )}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: brand.c.navy }}>
              Admin Panel
            </div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              Manage prescriptions and orders
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{
            padding: '4px 10px',
            background: '#FEF3C7',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            color: '#92400E'
          }}>
            {pendingRxs.length} pending Rx
          </div>
          <div style={{
            padding: '4px 10px',
            background: '#EFF6FF',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            color: '#1E40AF'
          }}>
            {activeOrders.length} active orders
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 4,
        padding: '0 24px',
        borderBottom: '2px solid #E5E7EB',
        background: '#F9FAFB'
      }}>
        {[
          { id: 'prescriptions', label: 'Prescriptions', count: prescriptions.length },
          { id: 'orders', label: 'Orders', count: orders.length },
          { id: 'messages', label: 'Messages', count: unreadThreads.length },
          { id: 'analytics', label: 'Analytics' }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'transparent',
              borderBottom: tab === t.id ? `3px solid ${brand.c.accent}` : '3px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              color: tab === t.id ? brand.c.accent : '#6B7280',
              transition: 'all 0.2s'
            }}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span style={{
                marginLeft: 6,
                padding: '2px 6px',
                borderRadius: 10,
                background: tab === t.id ? brand.c.accent : '#E5E7EB',
                color: tab === t.id ? 'white' : '#6B7280',
                fontSize: 11,
                fontWeight: 700
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {tab === 'prescriptions' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {['all', 'pending_review', 'verified', 'rejected'].map(status => (
                <button
                  key={status}
                  onClick={() => setRxFilter(status)}
                  style={{
                    padding: '6px 14px',
                    border: '2px solid',
                    borderColor: rxFilter === status ? brand.c.accent : '#E5E7EB',
                    borderRadius: 20,
                    background: rxFilter === status ? `${brand.c.accent}15` : 'white',
                    color: rxFilter === status ? brand.c.accent : '#6B7280',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {status === 'all' ? 'All' : status === 'pending_review' ? 'Pending Review' : status.charAt(0).toUpperCase() + status.slice(1)} ({status === 'all' ? prescriptions.length : prescriptions.filter(rx => rx.status === status).length})
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {filteredRxs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6B7280' }}>
                  <Icon name="checkCircle" size={48} color="#10B981" style={{ marginBottom: 12 }} />
                  <p style={{ fontSize: 16, fontWeight: 600 }}>No prescriptions</p>
                  <p style={{ fontSize: 14 }}>No prescriptions match the selected filter</p>
                </div>
              ) : (
                filteredRxs.map(rx => (
                  <React.Fragment key={rx.id}>
                    <div
                      style={{
                        padding: 16,
                        background: 'white',
                        border: '2px solid #E5E7EB',
                        borderRadius: 12,
                        display: 'flex',
                        gap: 16,
                        alignItems: 'start'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: brand.c.navy }}>
                            {rx.medication}
                          </div>
                          <Badge status={rx.status} />
                        </div>
                        <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                          <strong>Patient:</strong> {rx.userId}
                        </div>
                        <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                          <strong>Prescriber:</strong> {rx.prescriber} ({rx.prescriberPhone})
                        </div>
                        <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                          <strong>Quantity:</strong> {rx.quantity}
                        </div>
                        {rx.refills !== undefined && (
                          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                            <strong>Refills:</strong> {rx.refills}
                          </div>
                        )}
                        <div style={{ fontSize: 13, color: '#9CA3AF' }}>
                        Uploaded: {new Date(rx.uploadedAt).toLocaleString()}
                        </div>
                      </div>
                    
                      {rx.file && (
                        <div style={{ width: 120, flexShrink: 0 }}>
                          <img 
                            src={rx.file} 
                            alt="Prescription"
                            style={{
                              width: '100%',
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #E5E7EB'
                            }}
                          />
                        </div>
                      )}
                    
                      {rx.productSku && (
                        <div style={{
                          padding: '8px 12px',
                          background: '#F0FDF4',
                          border: '1px solid #BBF7D0',
                          borderRadius: 8,
                          fontSize: 13,
                          flexShrink: 0,
                          maxWidth: 200
                        }}>
                          <div style={{ fontWeight: 700, color: '#065F46', marginBottom: 2 }}>Mapped Product</div>
                          <div style={{ color: '#047857', fontSize: 12, marginBottom: 2 }}>{rx.productName}</div>
                          <div style={{ color: '#065F46', fontWeight: 600 }}>${(rx.productPrice || 0).toFixed(2)}</div>
                          <div style={{ fontSize: 11, color: '#6B7280' }}>SKU: {rx.productSku}</div>
                          {(rx.status === 'pending_review' || rx.status === 'under_review') && (
                            <button
                              onClick={() => unmapProductFromRx(rx.id)}
                              style={{
                                marginTop: 4, fontSize: 11, color: '#EF4444', background: 'none',
                                border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0
                              }}
                            >
                            Remove mapping
                            </button>
                          )}
                        </div>
                      )}

                      {(rx.status === 'pending_review' || rx.status === 'under_review') && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                          {!rx.productSku && (
                            <Button
                              size="sm"
                              icon="search"
                              variant="ghost"
                              onClick={() => {
                                setExpandedRx(expandedRx === rx.id ? null : rx.id);
                                if (!productSearch[rx.id]) {
                                  setProductSearch(prev => ({ ...prev, [rx.id]: rx.medication || '' }));
                                  searchProducts(rx.id, rx.medication || '');
                                }
                              }}
                            >
                            Map Product
                            </Button>
                          )}
                          <Button
                            size="sm"
                            icon="check"
                            onClick={() => verifyPrescription(rx.id)}
                            disabled={!rx.productSku}
                          >
                          Verify
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            icon="x"
                            onClick={() => rejectPrescription(rx.id)}
                          >
                          Reject
                          </Button>
                        </div>
                      )}
                    </div>

                    {expandedRx === rx.id && (rx.status === 'pending_review' || rx.status === 'under_review') && (
                      <div style={{
                        padding: 16,
                        background: '#F9FAFB',
                        border: '2px solid #E5E7EB',
                        borderTop: 'none',
                        borderRadius: '0 0 12px 12px',
                        marginTop: -12
                      }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: brand.c.navy, marginBottom: 8 }}>
                        Search Product Catalog
                        </div>
                        <input
                          type="text"
                          value={productSearch[rx.id] || ''}
                          onChange={e => setProductSearch(prev => ({ ...prev, [rx.id]: e.target.value }))}
                          placeholder="Search by medication name..."
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '2px solid #E5E7EB',
                            borderRadius: 8,
                            fontSize: 13,
                            fontFamily: 'Libre Franklin, sans-serif',
                            marginBottom: 8,
                            boxSizing: 'border-box'
                          }}
                        />
                        {productSearchLoading[rx.id] && (
                          <div style={{ fontSize: 13, color: '#6B7280', padding: '8px 0' }}>Searching...</div>
                        )}
                        {!productSearchLoading[rx.id] && (productResults[rx.id] || []).length === 0 && productSearch[rx.id] && (
                          <div style={{ fontSize: 13, color: '#6B7280', padding: '8px 0' }}>No products found</div>
                        )}
                        <div style={{ display: 'grid', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                          {(productResults[rx.id] || []).map(product => (
                            <button
                              key={product.id}
                              onClick={() => mapProductToRx(rx.id, product)}
                              style={{
                                display: 'flex',
                                gap: 10,
                                alignItems: 'center',
                                padding: '8px 12px',
                                background: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: 8,
                                cursor: 'pointer',
                                textAlign: 'left',
                                width: '100%',
                                transition: 'border-color 0.2s'
                              }}
                            >
                              {product.thumbnailUrl && (
                                <img src={product.thumbnailUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: brand.c.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {product.title}
                                </div>
                                <div style={{ fontSize: 11, color: '#6B7280' }}>
                                SKU: {product.sku} {product.packaging && `| ${product.packaging}`}
                                </div>
                              </div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: brand.c.accent, flexShrink: 0 }}>
                              ${(product.price?.price || product.price?.specialPrice || 0).toFixed(2)}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        )}
        
        {tab === 'orders' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {['all', 'submitted', 'processing', 'shipped', 'delivered'].map(status => (
                <button
                  key={status}
                  onClick={() => setOrderFilter(status)}
                  style={{
                    padding: '6px 14px',
                    border: '2px solid',
                    borderColor: orderFilter === status ? brand.c.accent : '#E5E7EB',
                    borderRadius: 20,
                    background: orderFilter === status ? `${brand.c.accent}15` : 'white',
                    color: orderFilter === status ? brand.c.accent : '#6B7280',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {status} {status === 'all' ? `(${orders.length})` : `(${orders.filter(o => o.status === status).length})`}
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {filteredOrders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6B7280' }}>
                  <Icon name="package" size={48} color="#9CA3AF" style={{ marginBottom: 12 }} />
                  <p style={{ fontSize: 16, fontWeight: 600 }}>No orders</p>
                  <p style={{ fontSize: 14 }}>No orders match the selected filter</p>
                </div>
              ) : (
                filteredOrders.map(order => (
                  <div
                    key={order.id}
                    style={{
                      padding: 16,
                      background: 'white',
                      border: '2px solid #E5E7EB',
                      borderRadius: 12,
                      display: 'flex',
                      gap: 16,
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: brand.c.navy }}>
                          {order.id}
                        </div>
                        <Badge status={order.status} />
                      </div>
                      <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                        <strong>Patient:</strong> {order.userId}
                      </div>
                      <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                        <strong>Items:</strong> {order.items?.length || order.prescriptionIds?.length || 0} prescription(s)
                      </div>
                      <div style={{ fontSize: 13, color: '#9CA3AF' }}>
                        Created: {new Date(order.createdAt).toLocaleString()}
                      </div>
                    </div>
                    
                    {order.status !== 'delivered' && (
                      <Button
                        size="sm"
                        icon="arrowRight"
                        onClick={() => advanceOrderStatus(order.id)}
                      >
                        {order.status === 'submitted' && 'Process'}
                        {order.status === 'processing' && 'Ship'}
                        {order.status === 'shipped' && 'Deliver'}
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {tab === 'messages' && (
          <div style={{ display: 'grid', gridTemplateColumns: messageThreads.length > 0 ? '240px 1fr' : '1fr', gap: 16 }}>
            {messageThreads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6B7280' }}>
                <Icon name="msgCircle" size={48} color="#9CA3AF" style={{ marginBottom: 12 }} />
                <p style={{ fontSize: 16, fontWeight: 600 }}>No messages</p>
                <p style={{ fontSize: 14 }}>No patient messages yet</p>
              </div>
            ) : (
              <>
                <div style={{ borderRight: '1px solid #E5E7EB', paddingRight: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#6B7280', marginBottom: 8, textTransform: 'uppercase' }}>Conversations</div>
                  {messageThreads.map(thread => {
                    const lastMsg = thread.messages[thread.messages.length - 1];
                    const isUnread = lastMsg && lastMsg.sender === 'patient';
                    return (
                      <button
                        key={thread.id}
                        onClick={() => setSelectedThread(thread)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: 10,
                          border: 'none',
                          borderRadius: 8,
                          background: selectedThread?.id === thread.id ? `${brand.c.accent}15` : 'transparent',
                          cursor: 'pointer',
                          textAlign: 'left',
                          marginBottom: 4
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 14, color: brand.c.navy }}>{thread.subject}</span>
                          {isUnread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: brand.c.accent, display: 'inline-block' }} />}
                        </div>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>
                          {thread.userId} - {thread.messages.length} msg{thread.messages.length !== 1 ? 's' : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div>
                  {selectedThread ? (
                    <>
                      <div style={{ borderBottom: '1px solid #E5E7EB', paddingBottom: 8, marginBottom: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: brand.c.navy }}>{selectedThread.subject}</div>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>Patient: {selectedThread.userId}</div>
                      </div>
                      <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 12 }}>
                        {selectedThread.messages.map(msg => (
                          <div key={msg.id} style={{ display: 'flex', justifyContent: msg.sender === 'patient' ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
                            <div style={{
                              maxWidth: '70%',
                              padding: '8px 12px',
                              borderRadius: 10,
                              background: msg.sender === 'patient' ? '#F3F4F6' : brand.c.accent,
                              color: msg.sender === 'patient' ? '#374151' : 'white',
                              fontSize: 13
                            }}>
                              <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 2 }}>
                                {msg.sender === 'patient' ? 'Patient' : 'Admin'}
                              </div>
                              <div>{msg.content}</div>
                              <div style={{ fontSize: 10, opacity: 0.7, textAlign: 'right', marginTop: 4 }}>
                                {new Date(msg.timestamp).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="text"
                          value={adminReply}
                          onChange={e => setAdminReply(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleAdminReply()}
                          placeholder="Type admin reply..."
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '2px solid #E5E7EB',
                            borderRadius: 8,
                            fontSize: 13,
                            fontFamily: 'Libre Franklin, sans-serif'
                          }}
                        />
                        <Button size="sm" icon="send" onClick={handleAdminReply} disabled={!adminReply.trim()}>Reply</Button>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF', fontSize: 14 }}>
                      Select a conversation to view and reply
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        
        {tab === 'analytics' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            <Card>
              <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8, fontWeight: 600 }}>
                Total Prescriptions
              </div>
              <div style={{
                fontSize: 48,
                fontWeight: 900,
                color: brand.c.accent,
                fontFamily: "'Merriweather', serif"
              }}>
                {prescriptions.length}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                {pendingRxs.length} pending review
              </div>
            </Card>
            
            <Card>
              <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8, fontWeight: 600 }}>
                Total Orders
              </div>
              <div style={{
                fontSize: 48,
                fontWeight: 900,
                color: '#7C3AED',
                fontFamily: "'Merriweather', serif"
              }}>
                {orders.length}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                {activeOrders.length} in progress
              </div>
            </Card>
            
            <Card>
              <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8, fontWeight: 600 }}>
                Fulfillment Rate
              </div>
              <div style={{
                fontSize: 48,
                fontWeight: 900,
                color: '#10B981',
                fontFamily: "'Merriweather', serif"
              }}>
                99.2%
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                Last 30 days
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
